import { PauseRounded, PlayArrowRounded, ReplayRounded } from '@mui/icons-material';
import { Button, CircularProgress } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { BOARD_LAYOUT } from '../../boardLayout';
import { fetchSummary } from '../../services/GameSlice';
import { resultLink } from '../../socket';
import { chipColor, tokens } from '../../theme';
import ShareButtons from '../Share/ShareButtons';
import '../Board/board.css';
import { BoardFace } from '../Board/CardFace';
import usePhone, { usePhoneTable } from '../../usePhone';

const STEP_MS = 420;

const cardLabel = (card) => {
    if (!card || card === 'blank_card') {
        return 'Free corner';
    }
    const [suit, ...rest] = String(card).split('_');
    const value = rest.join(' ').replace('ace simple', 'ace');
    const title = (word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
    return `${title(value)} of ${title(suit)}`;
};

const describe = (summary) => {
    if (summary.isDraw) {
        return { title: "It's a draw", detail: 'Nobody could make another move.' };
    }
    if (!summary.winner) {
        return { title: 'Sequence', detail: '' };
    }
    const losers = summary.players.filter(p => p.team !== summary.winner.team).map(p => p.name);
    return {
        title: `${summary.winner.team} team won`,
        detail: `${summary.winner.names.join(' & ')} beat ${losers.join(' & ')} in ${summary.moveCount} moves.`
    };
};

/** Where shared result links land: the game played back, then a way in. */
const Replay = () => {
    const { gameId } = useParams();
    // Shared results are mostly opened on phones: same phone board as the game.
    const phone = usePhone();
    usePhoneTable(phone);
    const dispatch = useDispatch();
    const [summary, setSummary] = useState(null);
    const [failed, setFailed] = useState(false);
    const [step, setStep] = useState(0);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        dispatch(fetchSummary(gameId, (data) => {
            setSummary(data);
            setStep(0);
            setPlaying(data.history.length > 0);
        }, () => setFailed(true)));
    }, [gameId, dispatch]);

    const history = useMemo(() => summary?.history || [], [summary]);
    const total = history.length;

    useEffect(() => {
        if (!playing) {
            return;
        }
        if (step >= total) {
            setPlaying(false);
            return;
        }
        const timer = setTimeout(() => setStep(current => current + 1), STEP_MS);
        return () => clearTimeout(timer);
    }, [playing, step, total]);

    // The finished board carries which chips form the winning lines, so it is
    // shown as-is once the replay reaches the end (or when there is no history).
    const chips = useMemo(() => {
        if (!summary) {
            return new Map();
        }
        if (total === 0 || step >= total) {
            return new Map(summary.moves.map(m => [m.pos, m]));
        }
        const board = new Map();
        history.slice(0, step).forEach(entry => {
            if (entry.kind === 'remove') {
                board.delete(entry.pos);
            } else {
                board.set(entry.pos, entry);
            }
        });
        return board;
    }, [summary, history, step, total]);

    const last = step > 0 && step <= total ? history[step - 1] : null;

    if (failed) {
        return (
            <div className="table replay-empty">
                <h1>This game isn't available</h1>
                <p>It may still be in progress, or the link is wrong.</p>
                <Button component={Link} to="/" variant="contained" color="secondary" size="large">Play Sequence</Button>
            </div>
        );
    }

    if (!summary) {
        return <div className="table replay-empty"><CircularProgress sx={{ color: tokens.brass }} /></div>;
    }

    if (summary.status !== 'COMPLETED') {
        return (
            <div className="table replay-empty">
                <h1>This game is still being played</h1>
                <p>Come back once it's over to watch it back.</p>
                <Button component={Link} to="/" variant="contained" color="secondary" size="large">Start your own game</Button>
            </div>
        );
    }

    const text = describe(summary);
    const finished = total === 0 || step >= total;

    return (
        <div className="table">
            <Helmet>
                <title>{`${text.title} | Play Sequence`}</title>
                <meta name="description" content={`${text.detail} Watch the game back, then play Sequence free in your browser.`} />
            </Helmet>
            <header className="topbar">
                <div className="brand">
                    <Link to="/" className="brand-name" aria-label="Play Sequence home">
                        <span className="brand-mark" aria-hidden="true"><span /><span /><span /></span>
                        Play Sequence
                    </Link>
                </div>
                <div className="turn">
                    <div className={`turn-banner${finished ? ' done' : ''}`}>
                        {finished ? text.title : `Move ${step} of ${total}`}
                    </div>
                </div>
                <div className="actions">
                    <Button component={Link} to="/" variant="contained" color="secondary">Play now</Button>
                </div>
            </header>

            <main className={phone ? 'play phone-main replay-phone' : 'play'}>
                <div className="board" role="grid" aria-label="Replay board">
                    {Array.from({ length: 10 }).map((_, i) => Array.from({ length: 10 }).map((__, j) => {
                        const pos = `${i},${j}`;
                        const chip = chips.get(pos);
                        const card = (summary.layout || BOARD_LAYOUT)[pos];
                        return (
                            <div
                                key={pos}
                                role="gridcell"
                                className={`cell${chip?.completedSequence ? ' in-sequence' : ''}${card === 'blank_card' ? ' corner' : ''}`}
                            >
                                {phone
                                    ? <BoardFace card={card}/>
                                    : <img src={`https://d5hzzxzqjp9y1.cloudfront.net/cardfronts/${card}.png`} alt={cardLabel(card)} draggable={false} />}
                                {chip && <div
                                    className={`chip${last?.pos === pos && last.kind === 'place' ? ' last-move' : ''}`}
                                    style={{ '--c': chipColor(chip.team) || '#8A958F' }}
                                />}
                                {last?.pos === pos && last.kind === 'remove' && <div className="removed-chip" style={{ color: chipColor(last.team) }} />}
                            </div>
                        );
                    }))}
                </div>

                <aside className="panel">
                    <section className="panel-section game-over">
                        <div className="game-over-title">{text.title}</div>
                        <div className="you-name">{text.detail}</div>
                        {total > 0 && <>
                            <div className="replay-progress" aria-hidden="true">
                                <span style={{ width: `${(step / total) * 100}%` }} />
                            </div>
                            <div className="game-over-actions">
                                {finished
                                    ? <Button size="small" variant="outlined" startIcon={<ReplayRounded />} sx={{ color: tokens.parchment, borderColor: 'rgba(243, 238, 226, 0.3)' }}
                                        onClick={() => { setStep(0); setPlaying(true); }}>Watch again</Button>
                                    : <Button size="small" variant="outlined" startIcon={playing ? <PauseRounded /> : <PlayArrowRounded />} sx={{ color: tokens.parchment, borderColor: 'rgba(243, 238, 226, 0.3)' }}
                                        onClick={() => setPlaying(!playing)}>{playing ? 'Pause' : 'Play'}</Button>}
                                {!finished && <Button size="small" sx={{ color: tokens.parchmentMuted }} onClick={() => { setPlaying(false); setStep(total); }}>Skip to the end</Button>}
                            </div>
                        </>}
                    </section>

                    <section className="panel-section">
                        <h2 className="panel-heading">Players</h2>
                        <ul className="players">
                            {summary.players.map((player, i) => (
                                <li key={`${player.name}-${i}`} className="player">
                                    <span className="chip" style={{ '--c': chipColor(player.team) || '#8A958F' }} aria-hidden="true" />
                                    <span className="player-name">{player.name}</span>
                                    <span className="player-meta">{player.isBot ? 'Computer' : player.team}</span>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className="panel-section cta">
                        <h2 className="panel-heading">Your turn</h2>
                        <p className="hint">Sequence is free in your browser. Play a friend or the computer, no sign-up.</p>
                        <Button component={Link} to="/" variant="contained" color="secondary" size="large" fullWidth>Play Sequence now</Button>
                        <ShareButtons url={resultLink(summary.id)} text={`${text.title}. ${text.detail} Watch it back:`} kind="result" itemId={summary.id} tone="dark" />
                    </section>
                </aside>
            </main>
        </div>
    );
};

export default Replay;
